import { useCallback, useEffect, useMemo, useState } from "react";
import { DynamicDatePicker } from "../../../components/DatePicker";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { BrushStart } from "../../../models/chart_model";
import {
  getGridcoDiscomData,
  getGridcoScheduleData,
  getDiscomLoadMetrics, 
} from "../../../Rest_api/RestApi";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { GROUP_BY } from "./Data";
import { GridcoDiscomData } from "../../../models/gridco";
import { DISCOM } from "./Data";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { DiscomGridcoData } from "../../../models/metrics";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
const BLOCKS_96 = Array.from({ length: 96 }, (_, i) => i + 1);

const UNITS = ["MW", "MWh", "LU"];

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
function formatMonthYear(dateStr: string): string {
  const d = new Date(dateStr);
  return `${MONTH_NAMES[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
}

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "dateIndex",
    values: ["dateIndex"],
    isTime: false,
  },
  // {
  //   label: "Day",
  //   key: "date",
  //   values: ["date"],
  //   isTime: false,
  // },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

export function GridcoTrend() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [drawalData, setDrawalData] = useState<GridcoDiscomData[]>([]);
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedInterval, setSelectedInterval] = useState(X_AXIS[0]);
    const [metrics, setMetrics] = useState<DiscomGridcoData | null>(null);
    const [metricsLoading, setMetricsLoading] = useState(false);
  const fetchMetrics = useCallback(async () => {
      if (!DefaultDates[activeTab]?.defaultTrendDates) {
        setMetrics(null);
        setMetricsLoading(false);
        return;
      }
      setMetricsLoading(true);
  
      try {
        const originalDates = DefaultDates[activeTab].defaultTrendDates;
  
        const ranges = originalDates
          .filter((date: any) => {
            // Skip forecast entries — value has 3 parts: "start,end,Model 1"
            const commaParts = date.value.split(",");
            return commaParts.length <= 2;
          })
          .map((date: any) => {
            let start: string;
            let end: string;
  
            if (date.value.includes(" to ")) {
              [start, end] = date.value
                .split(" to ")
                .map((s: string) => s.trim());
            } else {
              const parts = date.value.split(",").map((s: string) => s.trim());
              start = parts[0];
              end = parts[1] ?? parts[0];
            }
  
            return {
              startDate: formatDateForAPI(new Date(start)),
              endDate: formatDateForAPI(new Date(end)),
              blocks: selectedBlock.map((block: any) => block.value),
            };
          })
          .filter((range: any) => range.startDate && range.endDate);
        if (ranges.length === 0) {
          setMetrics(null);
          setMetricsLoading(false);
          return;
        }
        const metricsData = await getDiscomLoadMetrics({
          ranges,
          aggregated: true,
          discom: selectedDiscom.length > 0 ? selectedDiscom : undefined,
        });
  
        setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
      } catch (error) {
        console.error("Error fetching metrics:", error);
        setMetrics(null);
      } finally {
        setMetricsLoading(false);
      }
    }, [DefaultDates, activeTab, selectedBlock, selectedDiscom]);
    useEffect(() => {
      fetchMetrics();
    }, [fetchMetrics]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      if (selectedBlock.length > 0) {
        return "block";
      }
      return "block";
    } else {
      return selectedInterval.values[0];
    }
  }, [selectedInterval.key, selectedInterval.values, selectedBlock.length]);

  const chartSuffixes = [
    { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
    { suffix: "schedule", displayName: "Schedule", stackSuffix: "schedule" },
    // {
    //   suffix: "load_shedding",
    //   displayName: "Load Shedding",
    //   stackSuffix: "load_shedding",
    // },
    // { suffix: "total", displayName: "Total Load", stackSuffix: "total" },
  ];

  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    // chartSuffixes[1],
  ]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultTrendDates === undefined) return;
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultTrendDates;

    const promises = dates.map(async (date: any) => {
      const [startDate, endDate] = date.value.split(",");
      const [fetchedDrawal, fetchedSchedule] = await Promise.all([
        getGridcoDiscomData(startDate, endDate),
        getGridcoScheduleData(startDate, endDate),
      ]);
      return {
        drawal: fetchedDrawal.map((item: any) => ({
          ...item,
          dates: date.label,
        })),
        schedule: fetchedSchedule.map((item: any) => ({
          ...item,
          dates: date.label,
        })),
      };
    });

    const results = await Promise.all(promises);
    const allDrawal = results.flatMap((r) => r.drawal);
    const allSchedule = results.flatMap((r) => r.schedule);

    setDrawalData(allDrawal);
    setScheduleData(allSchedule);
    setUniqueBlock(Array.from(new Set(allDrawal.map((item) => item.block))));
    setLoading(false);
  }, [DefaultDates, activeTab]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
  }

  const handleKey = useCallback(() => {
    if (selectedBlock.length > 0) {
      if (selectedInterval.key === "time") return ["dateIndex", "block"];
      if (selectedInterval.key === "dateIndex") return ["dateIndex"];
      if (selectedInterval.key === "date") return ["dateIndex"];
      if (selectedInterval.key === "month") return ["month"];
    }
    if (selectedInterval.key === "time" || selectedInterval.key === "dateIndex") {
      return selectedInterval.values;
    }
    return selectedInterval.values;
  }, [selectedBlock, selectedInterval]);

  useEffect(() => {
    const isDrawalSelected = selectedSuffix.some((s) => s.suffix === "drawal");
    const isScheduleSelected = selectedSuffix.some(
      (s) => s.suffix === "schedule",
    );

    // Build relative day index per period (so periods overlay on the same x-axis)
    const periodDates = new Map<string, Set<string>>();
    for (const d of drawalData) {
      if (!periodDates.has(d.dates)) periodDates.set(d.dates, new Set());
      periodDates.get(d.dates)!.add(d.date);
    }
    for (const s of scheduleData) {
      if (!periodDates.has(s.dates)) periodDates.set(s.dates, new Set());
      periodDates.get(s.dates)!.add(s.date);
    }
    const dateIndexMap = new Map<string, string>(); // `${dates}|${date}` -> "Day 1","Day 2",...
    for (const [period, dateSet] of periodDates) {
      Array.from(dateSet)
        .sort()
        .forEach((date, i) =>
          dateIndexMap.set(`${period}|${date}`, `Day ${i + 1}`),
        );
    }

    // O(n+m) merge using Map
    const mergedMap = new Map<string, any>();

    for (const d of drawalData) {
      const key = `${d.date}|${d.block}|${d.discom}|${d.dates}`;
      mergedMap.set(key, {
        date: d.date,
        dateIndex: dateIndexMap.get(`${d.dates}|${d.date}`) ?? d.date,
        block: d.block,
        discom: d.discom,
        drawal_mw: d.drawal_mw,
        drawal_mwh: d.drawal_mwh,
        drawal_lu: d.drawal_mwh / 100,
        load_shedding_mw: d.load_shedding_mw,
        schedule_mw: 0,
        schedule_mwh: 0,
        schedule_lu: 0,
        month: formatMonthYear(d.date),
        dates: d.dates,
      });
    }

    for (const s of scheduleData) {
      const key = `${s.date}|${s.block}|${s.discom}|${s.dates}`;
      const existing = mergedMap.get(key);
      if (existing) {
        existing.schedule_mw = s.schedule_mw;
        existing.schedule_mwh = s.schedule_mwh;
        existing.schedule_lu = s.schedule_mwh / 100;
      } else {
        mergedMap.set(key, {
          date: s.date,
          dateIndex: dateIndexMap.get(`${s.dates}|${s.date}`) ?? 1,
          block: s.block,
          discom: s.discom,
          drawal_mw: 0,
          drawal_mwh: 0,
          drawal_lu: 0,
          load_shedding_mw: 0,
          schedule_mw: s.schedule_mw,
          schedule_mwh: s.schedule_mwh,
          schedule_lu: s.schedule_mwh / 100,
          month: formatMonthYear(s.date),
          dates: s.dates,
        });
      }
    }

    let filteredData = Array.from(mergedMap.values());

    // Filter by type
    if (isDrawalSelected && !isScheduleSelected) {
      filteredData = filteredData.filter((item) => item.drawal_mw > 0);
    } else if (!isDrawalSelected && isScheduleSelected) {
      filteredData = filteredData.filter((item) => item.schedule_mw > 0);
    }

    if (selectedDiscom.length > 0) {
      const discomSet = new Set(selectedDiscom);
      filteredData = filteredData.filter((item) => discomSet.has(item.discom));
    }
    if (selectedBlock.length > 0) {
      const blockSet = new Set(selectedBlock.map((b) => b.value));
      filteredData = filteredData.filter((item) => blockSet.has(item.block));
    }

    const aggregationKeys: string[] = [];
    const averageKeys: string[] = [];
    for (const { suffix } of selectedSuffix) {
      if (suffix === "drawal") {
        aggregationKeys.push("drawal_mw", "drawal_mwh", "drawal_lu");
        averageKeys.push("drawal_mw");
      }
      if (suffix === "schedule") {
        aggregationKeys.push("schedule_mw", "schedule_mwh", "schedule_lu");
        averageKeys.push("schedule_mw");
      }
    }

    const par: ChartDataParams = {
      keys: handleKey() || [],
      tableKeys: ["date", "block", "dateIndex"],
      groupBy: ["dates"].concat([selectedGroupKeys.toLowerCase()]),
      volumeKey: ``,
      aggregationKeys,
      averageKeys,
      averageDuplicationKeys:
        selectedInterval.key === "date"
          ? ["date", "block"]
          : ["dateIndex", "block"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);

    setChartData(cData.data);
    setTableData(cData.tableData || []);
    setChartKeys(cData.keys);
  }, [
    drawalData,
    scheduleData,
    selectedSuffix,
    selectedGroupKeys,
    selectedDiscom,
    selectedArea,
    selectedBlock,
    selectedInterval,
    selectedUnit,
    handleKey,
  ]);

  function _handleSuffixChange(index: number) {
    const selectedItem = chartSuffixes[index];
    if (selectedSuffix.some((item) => item.suffix === selectedItem.suffix)) {
      if (selectedSuffix.length === 1) return;
      setSelectedSuffix(
        selectedSuffix.filter((item) => item.suffix !== selectedItem.suffix),
      );
    } else {
      setSelectedSuffix([...selectedSuffix, selectedItem]);
    }
  }

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const legends = useMemo(
    () =>
      selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys.map((key) => ({
          dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
          name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
          type: (selectedChartType as any).value,
          stackId:
            (selectedChartType as any).stackId !== undefined
              ? stackSuffix
              : undefined,
        })),
      ),
    [selectedSuffix, chartKeys, selectedUnit, selectedChartType],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);
  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", "dateIndex"]
    : selectedInterval.key === "dateIndex"
      ? ["dateIndex"]
      : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.isTime
    ? ["block", "dateIndex"]
    : selectedInterval.key === "dateIndex"
      ? ["dateIndex"]
      : [selectedInterval.values[0]];

  const tablePar = {
    legends: visibleLegends,
    tableData: TableData,
    data: chartData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: "Discom Load Trend Analysis",
  };

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "dateIndex";
      }
      return "dateIndex";
    } else {
      return undefined;
    }
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Interval"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(X_AXIS[index]);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              handleUnitChange(item);
            }}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              _handleGroupByChange(index);
            }}
          />

          <FilterMenu
            title="Type"
            items={chartSuffixes.map((item) => item.displayName)}
            selectedItems={selectedSuffix.map((item) => item.displayName)}
            onSelect={(item, index) => {
              _handleSuffixChange(index);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            disabledItems={selectedGroupKeys === "Total" ? ["Stacked Bar"] : []}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
           <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Peak Load (MW)"].value,
                  unit: metrics.drawal["Peak Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Peak Load (MW)"].value,
                  unit: metrics.schedule["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Average Load (MW)"].value,
                  unit: metrics.drawal["Average Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Average Load (MW)"].value,
                  unit: metrics.schedule["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Minimum Load (MW)"].value,
                  unit: metrics.drawal["Minimum Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Minimum Load (MW)"].value,
                  unit: metrics.schedule["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Total Energy (MU)"].value,
                  unit: metrics.drawal["Total Energy (MU)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Total Energy (MU)"].value,
                  unit: metrics.schedule["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Demand Trend"
              subtitle="Fetching Gridco discom load trend data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">
                  Discom Load ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={BLOCKS_96}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);

                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);

                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>{" "}
              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={handlexDataKey()}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>Gridco : BSP bills & ABT Meters</p>}
              text="1. BSP Bills : Annexure 1 is used for cumulation.
              2. Some meter numbers in Annexure 1 were missing in provided ABT Meter files.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
